import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CheckCircle, Circle } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { BetterDocPatientSelectionDialog } from "@/modules/admin/better-doc/components/better-doc-patient-selection-dialog"
import { BetterDocReferralSlipDialog } from "@/modules/admin/better-doc/components/better-doc-referral-slip-dialog"
import { BetterDocSessionActionResult } from "@/modules/admin/better-doc/components/better-doc-session-action-result"
import { RECOMMENDED_BETTER_DOC_STATES } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { collectBetterDocErrorMessages } from "@/modules/admin/better-doc/lib/collect-better-doc-error-messages"
import { BetterDocSessionDetail } from "@/modules/admin/better-doc/services/betterDocSessionQuery"
import { useSimulateBetterDocResearchMutation } from "@/modules/admin/better-doc/services/useSimulateBetterDocResearchMutation"
import { useSimulateBetterDocSecondOpinionVisitConfirmationMutation } from "@/modules/admin/better-doc/services/useSimulateBetterDocSecondOpinionVisitConfirmationMutation"
import { BetterDocSessionActionOutcome } from "@/modules/admin/better-doc/types/BetterDocSessionActionOutcome"
import { SofiaEnvironment } from "@/sofia-environment"
import { de_BetterDocRecommendationState } from "@/types/__generated__/union-types"

type BetterDocMsnProgressCardProps = {
  detail: BetterDocSessionDetail
  liveBetterDocState: de_BetterDocRecommendationState | null
  actionOutcome: BetterDocSessionActionOutcome | null
  onActionCompleted: (outcome: BetterDocSessionActionOutcome) => void
}

export const BetterDocMsnProgressCard = ({
  detail,
  liveBetterDocState,
  actionOutcome,
  onActionCompleted,
}: BetterDocMsnProgressCardProps) => {
  const { t } = useTranslation()
  const [isReferralSlipDialogOpen, setIsReferralSlipDialogOpen] = useState(false)
  const [isPatientSelectionDialogOpen, setIsPatientSelectionDialogOpen] = useState(false)
  const [simulateResearch, isSimulatingResearch] = useSimulateBetterDocResearchMutation()
  const [simulateSecondOpinionVisitConfirmation, isSimulatingSecondOpinionVisitConfirmation] =
    useSimulateBetterDocSecondOpinionVisitConfirmationMutation()
  const { session, steps } = detail

  const isActionAvailable = SofiaEnvironment.CurrentEnvironment !== "production"
  const isSimulating = isSimulatingResearch || isSimulatingSecondOpinionVisitConfirmation
  const isSecondOpinionVisitConfirmationRequested = steps.some(
    (step) => step.type === "SECOND_OPINION_VISIT_CONFIRMATION_REQUESTED",
  )
  const hasSecondOpinionVisitStage =
    session.testCaseScenario === "MSN_WITH_SECOND_OPINION" || isSecondOpinionVisitConfirmationRequested

  const stages = [
    { key: "created", label: t("admin.betterDoc.msnProgress.stages.created"), isDone: true },
    {
      key: "referral-slip-sent",
      label: t("admin.betterDoc.msnProgress.stages.referralSlipSent"),
      isDone: steps.some((step) => step.type === "REFERRAL_SLIP_SENT"),
    },
    {
      key: "research-requested",
      label: t("admin.betterDoc.msnProgress.stages.researchRequested"),
      isDone: steps.some((step) => step.type === "RESEARCH_REQUESTED"),
    },
    {
      key: "recommended",
      label: t("admin.betterDoc.msnProgress.stages.recommended"),
      isDone: [session.betterDocState, liveBetterDocState].some(
        (betterDocState) => betterDocState !== null && RECOMMENDED_BETTER_DOC_STATES.includes(betterDocState),
      ),
    },
    {
      key: "patient-selection-requested",
      label: t("admin.betterDoc.msnProgress.stages.patientSelectionRequested"),
      isDone: steps.some((step) => step.type === "PATIENT_SELECTION_REQUESTED"),
    },
    ...(hasSecondOpinionVisitStage
      ? [
          {
            key: "second-opinion-visit-confirmation-requested",
            label: t("admin.betterDoc.msnProgress.stages.secondOpinionVisitConfirmationRequested"),
            isDone: isSecondOpinionVisitConfirmationRequested,
          },
        ]
      : []),
  ]

  const handleSimulateResearch = () => {
    simulateResearch({
      variables: { input: { sessionId: session.sessionId } },
      onCompleted: (response) => {
        const { actionResult, errors } = response.de_simulateBetterDocResearch
        onActionCompleted({ result: actionResult, errorMessages: collectBetterDocErrorMessages(errors) })
      },
    })
  }

  const handleSimulateSecondOpinionVisitConfirmation = () => {
    simulateSecondOpinionVisitConfirmation({
      variables: { input: { sessionId: session.sessionId } },
      onCompleted: (response) => {
        const { actionResult, errors } = response.de_simulateBetterDocSecondOpinionVisitConfirmation
        onActionCompleted({ result: actionResult, errorMessages: collectBetterDocErrorMessages(errors) })
      },
    })
  }

  return (
    <Card data-testid="better-doc-admin-msn-progress">
      <CardHeader>
        <CardHeaderTitle>{t("admin.betterDoc.msnProgress.title")}</CardHeaderTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-4 p-4 pt-0">
        <ol className="flex flex-wrap gap-6" data-testid="better-doc-admin-msn-progress-stages">
          {stages.map((stage) => (
            <li
              key={stage.key}
              className={cn(
                "flex items-center gap-2 text-sm",
                stage.isDone ? "text-on-surface" : "text-on-surface-subdued",
              )}
              data-testid={`better-doc-admin-msn-progress-stage-${stage.key}`}
              data-is-done={stage.isDone}>
              {stage.isDone ? (
                <CheckCircle weight="fill" className="text-success size-5" />
              ) : (
                <Circle className="size-5" />
              )}
              <span>{stage.label}</span>
              <span className="sr-only">
                {stage.isDone ? t("admin.betterDoc.msnProgress.done") : t("admin.betterDoc.msnProgress.open")}
              </span>
            </li>
          ))}
        </ol>
        {isActionAvailable ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!session.isTestCase || isSimulating}
                  onClick={() => setIsReferralSlipDialogOpen(true)}
                  data-testid="better-doc-admin-msn-send-referral-slip-button">
                  {t("admin.betterDoc.msnProgress.sendReferralSlip")}
                </Button>
                {session.isTestCase ? null : (
                  <span
                    className="text-on-surface-subdued text-xs"
                    data-testid="better-doc-admin-msn-send-referral-slip-hint">
                    {t("admin.betterDoc.msnProgress.referralSlipOnlyForTestCase")}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  isLoading={isSimulatingResearch}
                  disabled={session.recommendationId === null || isSimulating}
                  onClick={handleSimulateResearch}
                  data-testid="better-doc-admin-msn-simulate-research-button">
                  {t("admin.betterDoc.msnProgress.simulateResearch")}
                </Button>
                {session.recommendationId === null ? (
                  <span
                    className="text-on-surface-subdued text-xs"
                    data-testid="better-doc-admin-msn-simulate-research-hint">
                    {t("admin.betterDoc.noRecommendationYet")}
                  </span>
                ) : null}
              </div>
              <Button
                type="button"
                variant="secondary"
                disabled={session.recommendationId === null || isSimulating}
                onClick={() => setIsPatientSelectionDialogOpen(true)}
                data-testid="better-doc-admin-msn-simulate-patient-selection-button">
                {t("admin.betterDoc.msnProgress.simulatePatientSelection")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                isLoading={isSimulatingSecondOpinionVisitConfirmation}
                disabled={session.recommendationId === null || isSimulating}
                onClick={handleSimulateSecondOpinionVisitConfirmation}
                data-testid="better-doc-admin-msn-simulate-second-opinion-visit-confirmation-button">
                {t("admin.betterDoc.msnProgress.simulateSecondOpinionVisitConfirmation")}
              </Button>
            </div>
            <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-msn-progress-hint">
              {t("admin.betterDoc.msnProgress.hint")}
            </p>
            {actionOutcome === null ? null : <BetterDocSessionActionResult outcome={actionOutcome} />}
          </div>
        ) : null}
      </CardBody>
      {isReferralSlipDialogOpen ? (
        <BetterDocReferralSlipDialog
          sessionId={session.sessionId}
          onClose={() => setIsReferralSlipDialogOpen(false)}
          onCompleted={onActionCompleted}
        />
      ) : null}
      {isPatientSelectionDialogOpen && session.recommendationId !== null ? (
        <BetterDocPatientSelectionDialog
          sessionId={session.sessionId}
          recommendationId={session.recommendationId}
          onClose={() => setIsPatientSelectionDialogOpen(false)}
          onCompleted={onActionCompleted}
        />
      ) : null}
    </Card>
  )
}
